import { useSearchParams } from "react-router-dom";
import { useAccount } from "wagmi";
import { Flame, TrendingUp, Trophy } from "lucide-react";
import { useArticles } from "@/hooks/useArticles";
import { ArticleList } from "@/components/article/ArticleList";
import { CategoryBar } from "@/components/CategoryBar";
import { TopicChips } from "@/components/TopicChips";
import { Skeleton } from "@/components/ui/Skeleton";

const SORT_OPTIONS = [
  { key: "new", label: "New", icon: TrendingUp },
  { key: "hot", label: "Hot", icon: Flame },
  { key: "top", label: "Top", icon: Trophy },
] as const;

export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || undefined;
  const topic = searchParams.get("topic") || undefined;
  const subscribed = searchParams.get("subscribed") === "true";
  const sort = searchParams.get("sort") || "new";
  const { address } = useAccount();

  const setSort = (s: string) => {
    const next = new URLSearchParams(searchParams);
    if (s === "new") next.delete("sort");
    else next.set("sort", s);
    setSearchParams(next);
  };

  const { data: articles, isLoading } = useArticles({
    category: subscribed ? undefined : category,
    topic: subscribed ? undefined : topic,
    subscriberAddress: subscribed && address ? address : undefined,
    sort,
  });

  return (
    <div>
      <CategoryBar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">
            Pay-per-article publishing
          </h1>
          <p className="text-grey font-body text-lg">
            Read articles by paying writers directly with USDC or EURC via the
            x402 payment protocol.
          </p>
        </div>

        {!isLoading && articles && articles.length > 0 && !subscribed && (
          <TopicChips articles={articles} />
        )}

        <div className="flex items-center gap-1 mb-6">
          {SORT_OPTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-body font-bold transition-colors ${
                sort === key
                  ? "bg-orange text-white"
                  : "text-grey hover:text-black hover:bg-beige/10"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        ) : !articles?.length ? (
          <div className="py-20 text-center">
            <p className="text-grey font-body">
              {subscribed
                ? "No articles from writers you're subscribed to yet."
                : "No articles found."}
            </p>
          </div>
        ) : (
          <ArticleList articles={articles} />
        )}
      </div>
    </div>
  );
}
